import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';
import { ConfirmDisableDialogComponent } from './confirm-disable-dialog.component';
import { FeatureFlagDto, FeatureFlagService } from './feature-flag.service';
import { FlagEditDialogComponent } from './flag-edit-dialog.component';

import { AuthService } from './shared/services/auth/auth.service';
import { UserProfileStore } from './shared/store/user-profile.store';


export interface FeatureFlagRow {
  featureName: string;
  value: string;
  enabled: boolean;
  label: string;
  updatedAt: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTableModule,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private readonly userProfileStore = inject(UserProfileStore);
  
  protected readonly title = signal('Wells Feature Flags Manager');
  protected readonly userEmail = computed(() => this.userProfileStore.userProfile().email || 'No user');
  protected readonly isAuthenticated = computed(() => this.userProfileStore.isAuthenticated());
  protected readonly authErrorMessage = computed(() => this.userProfileStore.errorMessage());
  protected readonly apiBaseUrl = environment.appConfigurationApiBaseUrl;
  protected readonly displayedColumns = ['featureName', 'label', 'updatedAt', 'enabled'];
  protected readonly flags = signal<FeatureFlagRow[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly isSaving = signal<string | null>(null);
  protected readonly isAuthenticating = signal(true);

  constructor(
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly featureFlagService: FeatureFlagService,
    private readonly authService: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.startAuthentication();
  }

  protected async signIn(): Promise<void> {
    await this.startAuthentication();
  }

  private async startAuthentication(): Promise<void> {
    this.isAuthenticating.set(true);
    try {
      await this.authService.initializeAuth();
      if (this.userProfileStore.isAuthenticated()) {
        await this.loadFlags();
      }
    } catch {
      this.flags.set([]);
    } finally {
      this.isAuthenticating.set(false);
    }
  }

  protected async editFlag(flag: FeatureFlagRow): Promise<void> {
    const dialogRef = this.dialog.open(FlagEditDialogComponent, {
      width: '420px',
      data: { flag }
    });

    const newValue = await firstValueFrom(dialogRef.afterClosed());
    if (typeof newValue !== 'string') {
      return;
    }

    await this.persistFlagValue(flag, newValue.trim());
  }

  protected async toggleFlag(flag: FeatureFlagRow, event: MatSlideToggleChange): Promise<void> {
    const newEnabled = !flag.enabled;
    event.source.checked = flag.enabled;

    const dialogRef = this.dialog.open(ConfirmDisableDialogComponent, {
      width: '380px',
      data: { featureName: flag.featureName, enabling: newEnabled }
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    await this.persistFlagValue(flag, newEnabled.toString());
  }

  protected isFlagSaving(flagName: string): boolean {
    return this.isSaving() === flagName;
  }

  private async loadFlags(): Promise<void> {
    this.isLoading.set(true);
    try {
      const dtos = await this.featureFlagService.getFlags(this.apiBaseUrl);
      this.flags.set(dtos.map(this.mapDtoToRow));
    } catch {
      this.snackBar.open('Failed to load feature flags.', 'Dismiss', { duration: 5000 });
    } finally {
      this.isLoading.set(false);
    }
  }

  private async persistFlagValue(flag: FeatureFlagRow, newValue: string): Promise<void> {
    this.isSaving.set(flag.featureName);
    try {
      await this.featureFlagService.patchFlag(this.apiBaseUrl, flag.featureName, newValue, flag.label);

      this.flags.update((currentFlags) =>
        currentFlags.map((item) =>
          item.featureName === flag.featureName
            ? {
                ...item,
                value: newValue,
                enabled: newValue.toLowerCase() === 'true',
                updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
              }
            : item
        )
      );
    } catch {
      this.snackBar.open(`Could not update ${flag.featureName}.`, 'Dismiss', { duration: 5000 });
    } finally {
      this.isSaving.set(null);
    }
  }

  private mapDtoToRow(dto: FeatureFlagDto): FeatureFlagRow {
    return {
      featureName: dto.key,
      value: dto.value,
      enabled: dto.value.toLowerCase() === 'true',
      label: dto.label ?? '',
      updatedAt: dto.lastModified ?? ''
    };
  }
}





